import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const About = () => {
  useEffect(() => {
    // Set page title
    document.title = "About ValveSalesSupply - Industrial Equipment Buyers Texas Oklahoma Louisiana | 25+ Years Experience";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about ValveSalesSupply, trusted industrial equipment buyers serving refineries and petrochemical plants across 9 states since 1998. Professional service, above scrap rates, 25+ years experience in Texas, Oklahoma, Louisiana.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'industrial equipment buyers, surplus valve buyers, refinery equipment buyers Texas, petrochemical equipment buyers, ValveSalesSupply company, industrial equipment purchasing company');
    }

    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://valvesalessupply.com/about');

    // Add Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'About ValveSalesSupply - Industrial Equipment Buyers | 25+ Years Experience' },
      { property: 'og:description', content: 'Trusted industrial equipment buyers serving refineries across 9 states since 1998. Professional service, above scrap rates, complete documentation.' },
      { property: 'og:url', content: 'https://valvesalessupply.com/about' },
      { property: 'og:type', content: 'website' }
    ];

    ogTags.forEach(tag => {
      let meta = document.querySelector(`meta[property="${tag.property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', tag.property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', tag.content);
    });

    // Add Schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          "@id": "https://valvesalessupply.com/about/#webpage",
          "url": "https://valvesalessupply.com/about",
          "name": "About ValveSalesSupply - Industrial Equipment Buyers",
          "description": "Learn about ValveSalesSupply's 25+ years of experience purchasing surplus industrial equipment. Professional service across 9 states with above scrap market rates.",
          "mainEntity": {
            "@id": "https://valvesalessupply.com/#organization"
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://valvesalessupply.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "About",
                "item": "https://valvesalessupply.com/about"
              }
            ]
          }
        },
        {
          "@type": "Organization",
          "@id": "https://valvesalessupply.com/#organization",
          "name": "ValveSalesSupply",
          "url": "https://valvesalessupply.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://valvesalessupply.com/logo.png"
          },
          "description": "Professional industrial equipment purchasing company with over 25 years of experience serving refineries, chemical plants, and industrial facilities across 9 states.",
          "foundingDate": "1998",
          "numberOfEmployees": "15",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "TX",
            "addressCountry": "US"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-214-854-5638",
            "contactType": "customer service",
            "availableLanguage": "English"
          },
          "serviceArea": {
            "@type": "GeoShape",
            "addressRegion": ["TX", "OK", "LA", "NM", "KS", "ND", "SD", "WY", "UT"]
          },
          "knowsAbout": [
            "Industrial Equipment Valuation",
            "Surplus Equipment Purchasing",
            "Valve and Instrumentation Assessment",
            "Industrial Asset Recovery",
            "Process Control Equipment"
          ]
        },
        {
          "@type": "Person",
          "name": "Michael Rodriguez",
          "jobTitle": "Senior Equipment Appraiser",
          "worksFor": {
            "@id": "https://valvesalessupply.com/#organization"
          },
          "description": "25+ years in industrial equipment assessment with expertise in valve systems and process controls."
        },
        {
          "@type": "Person", 
          "name": "Sarah Thompson",
          "jobTitle": "Operations Manager",
          "worksFor": {
            "@id": "https://valvesalessupply.com/#organization"
          },
          "description": "Coordinates equipment pickups and logistics across our 9-state service area."
        },
        {
          "@type": "Person",
          "name": "David Chen",
          "jobTitle": "Technical Specialist",
          "worksFor": {
            "@id": "https://valvesalessupply.com/#organization"
          },
          "description": "Instrumentation expert specializing in control systems and measurement devices."
        },
        {
          "@type": "Person",
          "name": "Lisa Martinez",
          "jobTitle": "Client Relations",
          "worksFor": {
            "@id": "https://valvesalessupply.com/#organization"
          },
          "description": "Manages client relationships and ensures smooth transaction processes."
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How long has ValveSalesSupply been in business?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "ValveSalesSupply has been serving industrial facilities since 1998, with over 25 years of experience in purchasing surplus industrial equipment across 9 states."
              }
            },
            {
              "@type": "Question",
              "name": "What states does ValveSalesSupply serve?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We serve 9 states across the central United States: Texas, Oklahoma, Louisiana, New Mexico, Kansas, North Dakota, South Dakota, Wyoming, and Utah."
              }
            },
            {
              "@type": "Question",
              "name": "Is ValveSalesSupply licensed and insured?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we maintain full licensing, $5M general liability insurance, comprehensive workers compensation coverage, and comply with ISNetworld, OSHA, DOT, and EPA standards."
              }
            },
            {
              "@type": "Question",
              "name": "What makes ValveSalesSupply different from scrap buyers?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We pay above scrap market rates by recognizing equipment value and finding the right buyers. We provide professional documentation, coordinate with turnaround schedules, and offer industry expertise from former plant managers and maintenance supervisors."
              }
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const teamMembers = [
    {
      name: 'Robert Thompson',
      title: 'Founder & CEO',
      image: 'https://public.readdy.ai/ai/img_res/9845378a05822186bba6c260f56af246.jpg',
      bio: '25+ years in industrial equipment with extensive refinery and petrochemical experience.'
    },
    {
      name: 'Sarah Mitchell',
      title: 'Operations Director',
      image: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20operations%20manager%20industrial%20facility%20background%20confident%20professional%20industrial%20setting&width=400&height=400&seq=operations-director-002&orientation=squarish',
      bio: 'Expert in turnaround coordination and large-scale equipment removal projects.'
    },
    {
      name: 'Michael Rodriguez',
      title: 'Technical Specialist',
      image: 'https://readdy.ai/api/search-image?query=industrial%20engineer%20technical%20specialist%20with%20equipment%20background%20professional%20industrial%20setting%20expert%20technical%20professional&width=400&height=400&seq=technical-specialist-003&orientation=squarish',
      bio: 'Instrumentation expert specializing in process control and analytical equipment.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-white pt-32 sm:pt-24 pb-16 sm:pb-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=industrial%20facility%20headquarters%20professional%20business%20building%20with%20industrial%20equipment%20in%20foreground%20clean%20modern%20professional%20setting&width=1920&height=600&seq=company-headquarters-001&orientation=landscape')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3A5F]/80 to-[#1B3A5F]/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 text-center text-white">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">ABOUT VALVESALESSUPPLY</h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-200 max-w-3xl mx-auto">
            Trusted industrial equipment buyers serving America's energy infrastructure 
            since 1998 with professionalism, integrity, and above-market pricing.
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            {/* Images */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border-2 sm:border-4 lg:border-8 border-white">
                <img 
                  src="https://readdy.ai/api/search-image?query=industrial%20facility%20refinery%20petrochemical%20plant%20professional%20industrial%20equipment%20warehouse%20organized%20clean%20professional%20setting&width=600&height=400&seq=facility-image-001&orientation=landscape"
                  alt="Our Facility"
                  className="w-full h-40 sm:h-48 lg:h-64 object-cover"
                />
              </div>
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border-2 sm:border-4 lg:border-8 border-white">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20industrial%20team%20workers%20in%20safety%20gear%20at%20industrial%20facility%20experienced%20crew%20professional%20industrial%20setting&width=600&height=400&seq=team-image-002&orientation=landscape"
                  alt="Our Team"
                  className="w-full h-40 sm:h-48 lg:h-64 object-cover"
                />
              </div>
            </div>
            
            {/* Content */}
            <div className="lg:col-span-3 lg:pl-8">
              <p className="text-[#6B7280] uppercase text-xs sm:text-sm font-semibold mb-3 sm:mb-4 tracking-[2px]">
                ABOUT VALVESALESSUPPLY
              </p>
              <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 sm:mb-6 lg:mb-8 leading-tight">
                TRUSTED INDUSTRIAL EQUIPMENT BUYERS SINCE 1998
              </h2>
              
              <div className="space-y-3 sm:space-y-4 lg:space-y-6 text-gray-600 text-sm sm:text-base lg:text-lg leading-relaxed">
                <p>
                  For over 25 years, ValveSalesSupply has been the trusted partner for industrial 
                  facilities across the central United States. Founded by industry veterans who 
                  understood the need for professional, reliable equipment disposal services, 
                  we've grown to become the region's premier industrial equipment buyer.
                </p>
                
                <p>
                  Our company was built on the principle that surplus industrial equipment deserves 
                  more than scrap value. By leveraging our extensive network of buyers, refurbishers, 
                  and end users, we consistently deliver above-market pricing while providing the 
                  professional service that major industrial facilities require.
                </p>
                
                <p className="hidden lg:block">
                  What sets us apart is our deep understanding of industrial operations. Our team 
                  includes former plant managers, maintenance supervisors, and instrumentation 
                  specialists who speak your language and understand your challenges. We know that 
                  turnaround schedules are critical, documentation is essential, and safety is paramount.
                </p>
                
                <p className="hidden lg:block">
                  Today, we serve over 1,000 industrial facilities across 9 states, handling 
                  everything from small instrument packages to complete unit shutdowns. Our reputation 
                  for professionalism, fair pricing, and reliable service has made us the go-to 
                  choice for maintenance managers throughout the region.
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mt-6 sm:mt-8 lg:mt-10">
                {[
                  'Above scrap rate pricing',
                  'Professional documentation',
                  'Turnaround coordination',
                  'Licensed and insured'
                ].map((item, index) => (
                  <div key={index} className="flex items-center">
                    <i className="ri-check-line text-[#1B3A5F] mr-2 sm:mr-3 text-lg sm:text-xl"></i>
                    <span className="text-gray-700 font-medium text-sm sm:text-base">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/contact"
                className="bg-[#1B3A5F] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base lg:text-lg hover:bg-[#2B4A6F] transition-all duration-300 inline-flex items-center mt-6 sm:mt-8 lg:mt-10 whitespace-nowrap"
              >
                SPEAK WITH OUR TEAM
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">OUR LEADERSHIP TEAM</h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl">Industry veterans with decades of combined experience</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
            {teamMembers.map((member, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-4 sm:mb-6 lg:mb-8">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-48 sm:w-64 lg:w-80 h-48 sm:h-64 lg:h-80 mx-auto rounded-xl sm:rounded-2xl object-cover shadow-lg border-2 sm:border-4 lg:border-6 border-white"
                  />
                  <div className="absolute -bottom-1 sm:-bottom-2 lg:-bottom-4 -right-1 sm:-right-2 lg:-right-4 w-12 sm:w-16 lg:w-24 h-12 sm:h-16 lg:h-24 bg-[#1B3A5F] rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-white text-lg sm:text-xl lg:text-2xl"></i>
                  </div>
                </div>
                <h3 className="text-[#1B3A5F] text-lg sm:text-xl lg:text-2xl font-bold mb-2">{member.name}</h3>
                <p className="text-[#6B7280] text-sm sm:text-base lg:text-lg font-medium mb-3 sm:mb-4">{member.title}</p>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">OUR VALUES</h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl">The principles that guide everything we do</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                icon: 'ri-handshake-line',
                title: 'Integrity',
                description: 'Honest pricing, transparent processes, and keeping our commitments to every client.'
              },
              {
                icon: 'ri-award-line',
                title: 'Professionalism',
                description: 'Industry expertise, proper documentation, and service that meets industrial standards.'
              },
              {
                icon: 'ri-time-line',
                title: 'Reliability',
                description: 'Fast response times, on-schedule pickups, and consistent performance you can count on.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Safety',
                description: 'Full insurance coverage, certified crews, and strict adherence to safety protocols.'
              }
            ].map((value, index) => (
              <div key={index} className="bg-white rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 text-center hover:shadow-lg transition-all duration-300">
                <div className="w-12 sm:w-16 lg:w-20 h-12 sm:h-16 lg:h-20 bg-[#1B3A5F] rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 lg:mb-6">
                  <i className={`${value.icon} text-white text-xl sm:text-2xl lg:text-3xl`}></i>
                </div>
                <h3 className="text-[#1B3A5F] text-base sm:text-lg lg:text-xl font-bold mb-2 sm:mb-3 lg:mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#1B3A5F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">VALVESALESSUPPLY BY THE NUMBERS</h2>
            <p className="text-gray-200 text-base sm:text-lg lg:text-xl">Our track record of success and growth</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
                number: '25+',
                label: 'Years in Business',
                description: 'Serving industrial facilities since 1998'
              },
              {
                number: '1,000+',
                label: 'Facilities Served',
                description: 'Across 9 states in central America'
              },
              {
                number: '$50M+',
                label: 'Equipment Purchased',
                description: 'Total value of equipment acquired'
              },
              {
                number: '99%',
                label: 'Customer Satisfaction',
                description: 'Based on client feedback surveys'
              }
            ].map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-2">{stat.number}</div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold mb-2">{stat.label}</h3>
                <p className="text-gray-300 text-sm sm:text-base">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Compliance */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">CERTIFICATIONS & COMPLIANCE</h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl">Meeting industry standards for safety and operations</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                title: 'ISNetworld',
                description: 'Contractor management and compliance verification for industrial facilities.'
              },
              {
                title: 'OSHA Compliance',
                description: 'Full compliance with occupational safety and health administration standards.'
              },
              {
                title: 'DOT Certified',
                description: 'Department of Transportation certification for equipment transportation.'
              },
              {
                title: 'EPA Compliance',
                description: 'Environmental protection agency compliance for waste handling and disposal.'
              },
              {
                title: 'General Liability',
                description: '$5M general liability insurance covering all removal and transportation activities.'
              },
              {
                title: 'Workers Compensation',
                description: 'Comprehensive workers compensation coverage for all field personnel.'
              }
            ].map((cert, index) => (
              <div key={index} className="bg-[#F5F7FA] rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <div className="w-10 sm:w-12 lg:w-16 h-10 sm:h-12 lg:h-16 bg-[#1B3A5F] rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-shield-check-line text-white text-lg sm:text-xl lg:text-2xl"></i>
                </div>
                <h3 className="text-[#1B3A5F] text-sm sm:text-base lg:text-lg font-bold mb-2 sm:mb-3">{cert.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#1B3A5F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-6 sm:gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-2">
              <div className="text-[#6B7280] text-4xl sm:text-6xl lg:text-8xl mb-4 sm:mb-6">
                <i className="ri-double-quotes-l"></i>
              </div>
              <blockquote className="text-white text-base sm:text-lg lg:text-2xl leading-relaxed mb-4 sm:mb-6 lg:mb-8">
                "ValveSalesSupply has been our go-to partner for equipment disposal for over 10 years. 
                Their professionalism, fair pricing, and ability to work within our turnaround schedules 
                makes them invaluable to our operations."
              </blockquote>
              <div className="text-white">
                <div className="font-bold text-sm sm:text-base lg:text-lg">James Wilson</div>
                <div className="text-gray-300 text-xs sm:text-sm lg:text-base">Maintenance Manager</div>
                <div className="text-gray-300 text-xs sm:text-sm lg:text-base">Gulf Coast Refinery</div>
              </div>
            </div>
            
            <div className="lg:col-span-3 relative mt-6 sm:mt-8 lg:mt-0">
              <div className="bg-gray-200 rounded-xl sm:rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl border-2 sm:border-4 lg:border-8 border-white lg:ml-16">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20maintenance%20manager%20industrial%20facility%20background%20confident%20professional%20industrial%20setting%20refinery%20background&width=500&height=600&seq=testimonial-portrait-001&orientation=portrait"
                  alt="James Wilson"
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">FREQUENTLY ASKED QUESTIONS</h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl">Common questions about ValveSalesSupply</p>
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            {[
              {
                question: 'How long has ValveSalesSupply been in business?',
                answer: 'ValveSalesSupply has been serving industrial facilities since 1998, with over 25 years of experience in purchasing surplus industrial equipment. We have grown to become the premier equipment buyer across 9 states in the central United States.'
              },
              {
                question: 'What states does ValveSalesSupply serve?',
                answer: 'We serve 9 states across the central United States: Texas, Oklahoma, Louisiana, New Mexico, Kansas, North Dakota, South Dakota, Wyoming, and Utah. Our regional teams provide local service with national expertise.'
              },
              {
                question: 'Is ValveSalesSupply licensed and insured?',
                answer: 'Yes, we maintain full licensing and comprehensive insurance coverage including $5M general liability insurance and workers compensation for all field personnel. We comply with ISNetworld, OSHA, DOT, and EPA standards.'
              },
              {
                question: 'What makes ValveSalesSupply different from scrap buyers?',
                answer: 'We pay significantly above scrap market rates by recognizing the true value of industrial equipment and leveraging our extensive network of buyers and refurbishers. We also provide professional documentation, coordinate with turnaround schedules, and offer industry expertise from former plant managers and maintenance supervisors.'
              },
              {
                question: 'How many facilities has ValveSalesSupply worked with?',
                answer: 'We have served over 1,000 industrial facilities including refineries, petrochemical plants, and gas processing facilities. Our client base includes major operators throughout the central United States.'
              },
              {
                question: 'What certifications does ValveSalesSupply maintain?',
                answer: 'We maintain ISNetworld contractor management certification, OSHA compliance, DOT certification for transportation, EPA compliance for waste handling, and comprehensive insurance coverage for all operations.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-[#F5F7FA] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8">
                <h3 className="text-[#1B3A5F] text-base sm:text-lg lg:text-xl font-bold mb-2 sm:mb-3 lg:mb-4">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 lg:mb-6">READY TO WORK WITH US?</h2>
          <p className="text-gray-600 text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 lg:mb-10">
            Join over 1,000 industrial facilities who trust ValveSalesSupply for their equipment disposal needs.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <Link
              to="/contact"
              className="bg-[#1B3A5F] text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full text-base sm:text-lg lg:text-xl font-semibold hover:bg-[#2B4A6F] transition-all duration-300 whitespace-nowrap"
            >
              GET STARTED TODAY
            </Link>
            <a
              href="tel:(214) 854-5638"
              className="border-2 border-[#1B3A5F] text-[#1B3A5F] px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full text-base sm:text-lg lg:text-xl font-semibold hover:bg-[#1B3A5F] hover:text-white transition-all duration-300 flex items-center justify-center whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              CALL (214) 854-5638
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
